import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useFetchQuizResponse, type QuizResponse } from "@core/features/quiz";

type Props = {
  quizId: number;
};

/**
 * Platform-specific wrapper around useFetchQuizResponse.
 * Invalidates cached quiz data on mount to ensure fresh answers are displayed.
 *
 * Note: The core hook returns `quiz` typed as `Quiz` but it's actually
 * normalized to `QuizResponse` by normalizeQuizResponseData.
 */
export function useGetQuizResponse({ quizId }: Props) {
  const queryClient = useQueryClient();

  // Invalidate cache on mount to ensure fresh data is fetched
  // This handles the case where user navigates back to quiz after answering
  useEffect(() => {
    if (quizId) {
      queryClient.invalidateQueries({ queryKey: ["quiz-response", quizId] });
    }
  }, [queryClient, quizId]);

  const query = useFetchQuizResponse({ quizId });

  return {
    ...query,
    // The quiz is normalized to QuizResponse format
    quiz: query.data?.quiz as QuizResponse | undefined,
    quizResponse: query.data?.quizResponse,
  };
}

export default useGetQuizResponse;


